import { Color, Mesh, Vector2 } from 'three';

/**
 * Type of sphere being interacted with for hemisphere lights
 * - 'sky': Sky sphere (top) - drag to change sky color
 * - 'ground': Ground sphere (bottom) - drag to change ground color
 * - 'control': Control sphere (center) - click toggles on/off, drag changes intensity
 */
export type HemisphereSphereType = 'sky' | 'ground' | 'control' | null;

/**
 * State of light color picker interaction
 */
export interface LightInteractionState {
	/** Whether interaction is currently active (mouse down / touch) */
	isActive: boolean;
	/** The sphere mesh being interacted with */
	targetSphere: Mesh | null;
	/** Starting position of the drag */
	startPosition: Vector2;
	/** Current drag position */
	currentPosition: Vector2;
	/** Accumulated rotation angle in radians */
	rotationAngle: number;
	/** Temporary color during drag */
	tempColor: Color;
	/** Original color before interaction started */
	originalColor: Color;
	/** Original sphere scale before interaction */
	originalScale: number;
	/** For hemisphere lights: which sphere is being interacted with */
	hemisphereSphereType: HemisphereSphereType;
	/** Original light intensity before interaction started (for control sphere) */
	originalIntensity: number;
	/** Temporary intensity during drag (for control sphere) */
	tempIntensity: number;
	/** Whether this is a drag interaction (vs click) */
	isDragging: boolean;
}

/**
 * Configuration for light interaction
 */
export interface LightInteractionConfig {
	/** Scale multiplier when sphere is active (default: 1.1 = 10% larger) */
	activeScaleMultiplier?: number;
	/** Sensitivity of color rotation (default: 1.0) */
	rotationSensitivity?: number;
	/** Whether to show color preview on cone during drag (default: true) */
	showConePreview?: boolean;
}

/**
 * Callback when light color changes
 */
export type LightColorChangeCallback = (color: Color) => void;

/**
 * Callback when interaction starts (user begins dragging)
 */
export type InteractionStartCallback = () => void;

/**
 * Callback when interaction ends (user releases)
 */
export type InteractionEndCallback = () => void;

/**
 * Default configuration values
 */
export const LIGHT_INTERACTION_DEFAULTS = {
	ACTIVE_SCALE_MULTIPLIER: 1.1,
	ROTATION_SENSITIVITY: 3.0, // Higher = more responsive rotation
	SHOW_CONE_PREVIEW: true
} as const;

/**
 * Create initial interaction state
 */
export const createInitialInteractionState = (): LightInteractionState => ({
	isActive: false,
	targetSphere: null,
	startPosition: new Vector2(),
	currentPosition: new Vector2(),
	rotationAngle: 0,
	tempColor: new Color(0xffffff),
	originalColor: new Color(0xffffff),
	originalScale: 1,
	hemisphereSphereType: null,
	originalIntensity: 1,
	tempIntensity: 1,
	isDragging: false
});

